import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 250;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  connectedCallback() {
    super.connectedCallback();

    onDocumentReady(this.#preloadImages);
    this.#initMegaAccordion();
    this.#highlightActiveLink();
    this.#disableOurAppsClick();
  }

  #disableOurAppsClick() {
    const menuLinks = this.querySelectorAll(".menu-list__link");
    menuLinks.forEach((link) => {
      const linkText = link.textContent?.trim();
      const linkUrl = link.getAttribute("data-link-url") || link.getAttribute("href");
      
      if (linkText === "Our Apps" || linkUrl?.includes("/our-app")) {
        link.style.cursor = "default";
        link.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
      }
    });
  }

  #highlightActiveLink() {
    const currentPath = window.location.pathname;
    const menuLinks = this.querySelectorAll(".menu-list__link");

    menuLinks.forEach((link) => {
      const linkUrl =
        link.getAttribute("data-link-url") || link.getAttribute("href");
      if (!linkUrl) return;

      // Skip highlighting for "Our Apps" menu item
      const linkText = link.textContent?.trim();
      if (linkText === "Our Apps" || linkUrl.includes("/our-app")) {
        return;
      }

      // Remove any query strings or hashes for comparison
      const cleanLinkUrl = linkUrl.split("?")[0].split("#")[0];
      const cleanCurrentPath = currentPath.split("?")[0].split("#")[0];

      // Check if this link matches the current page
      if (
        cleanLinkUrl === cleanCurrentPath ||
        (cleanCurrentPath.includes(cleanLinkUrl) && cleanLinkUrl !== "/")
      ) {
        link.classList.add("menu-list__link--active");
      }
    });
  }

  #initMegaAccordion() {
    // Event delegation inside header-menu only
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('.mega-accordion__trigger');
      if (!trigger) return;

      const accordion = trigger.closest('.mega-accordion');
      if (!accordion) return;

      const content = accordion.querySelector('.mega-accordion__content');
      if (!content) return;

      event.preventDefault();
      event.stopPropagation();

      const isOpen = accordion.classList.contains('is-open');

      this.querySelectorAll('.mega-accordion.is-open').forEach((openAccordion) => {
        if (openAccordion !== accordion) {
          openAccordion.classList.remove('is-open');
          const openTrigger = openAccordion.querySelector('.mega-accordion__trigger');
          openTrigger?.setAttribute('aria-expanded', 'false');
        }
      });

      accordion.classList.toggle('is-open');
      trigger.setAttribute('aria-expanded', String(!isOpen));
    });
  }


  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();

    if (this.#state.activeItem) {
      this.#activateHandler(event);
    } else {
      this.#debouncedActivateHandler(event);
    }
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);    

    if (!item || item == this.#state.activeItem) return;

    this.dataset.overflowExpanded = (event.target.slot !== '').toString();

    this.#deactivate();
    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    // ✅ Get x position of the menu item
    const x = item.getBoundingClientRect().x;

    // ✅ Pass x to findSubmenu
    const submenu = findSubmenu(item, x);

    if (!submenu) return;
    submenu?.parentElement?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
  this.#debouncedActivateHandler.cancel();

  if (!(event.target instanceof Element)) {
    // If event.target is not an element, deactivate anyway
    this.#debouncedDeactivate(event);
    return;
  }

  const item = findMenuItem(event.target);
  console.log("item", item);

  if (item === this.#state.activeItem) {
    this.#debouncedDeactivate(event);
  } else if (!item && this.#state.activeItem) {
    // New: If no item under pointer, but menu is open, close it
    this.#debouncedDeactivate(event);
  }
}


  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce((event) => {
  this.dataset.overflowExpanded = 'false';

  const item = findMenuItem(event.target);
  console.log("item1111", item);

  if (!item && this.#state.activeItem) {
    this.#deactivate();
    return;
  }

  if (item != this.#state.activeItem) return;

  this.#deactivate(item);
}, DEACTIVATE_DELAY);


  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    const submenu = findSubmenu(item) 
    submenu?.parentElement?.style.setProperty('--submenu-height', '0px');

    if (!item) return;

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }
 // Check self first, then ancestors
  if (element.hasAttribute('data-menu') && element.getAttribute('data-menu') === 'menuitem') {
    return element;
  }
  return element?.querySelector('[data-menu="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element, x) {
  const clickedId = element?.dataset?.id;
  const menuBar = document.querySelectorAll('[ref="submenu[]"]');

  // Hide all submenus
  menuBar?.forEach(megamenu => {
    megamenu.classList.add('hidden');
  });

  let matchedSubmenu = null;

  // Show the matched submenu
  menuBar?.forEach(megamenu => {
    const submenuId = megamenu.getAttribute('data-id');
    if (clickedId === submenuId) {
      megamenu.classList.remove('hidden');
      matchedSubmenu = megamenu;
      // ✅ Now x is defined — use it
      if (x !== undefined) {
        matchedSubmenu.style.setProperty('--anchor-x', `${x}px`);
      }
    }
  });

  return matchedSubmenu instanceof HTMLElement ? matchedSubmenu : null;
}




document.querySelectorAll('.menu-drawer__menu-item').forEach(item => {
  item.addEventListener('click', function (e) {
    const submenu2 = findSubmenu(item);
    console.log('Submenu found:', submenu2);
  });
});


document.addEventListener('DOMContentLoaded', function () {
  const detailsList = document.querySelectorAll('accordion-custom details');

  detailsList.forEach((details) => {
    details.addEventListener('toggle', function () {
      if (details.open) {
        // Close all other open details
        detailsList.forEach((other) => {
          if (other !== details) {
            other.removeAttribute('open');
          }
        });

        // Scroll .details-content to top reliably
        const content = details.querySelector('.details-content');
        if (content) {
          // Ensure DOM has updated before scrolling
          requestAnimationFrame(() => {
            setTimeout(() => {
              content.scrollTop = 0;
            }, 50); // Small delay helps in real devices
          });
        }
      }
    });
  });
});

  